import { intersectionBy } from 'lodash';
import { useRouter } from 'next/router';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTrackCanvasMediaImported, useTrackShareClicked } from '@air/analytics';
import { type Board, type Clip, type Tag } from '@air/api/types';
import {
  buildNodesFromPayloads,
  CANVAS_IMAGE_NODE_SPACING,
  clipToCanvasAssetPayload,
  commands,
  DEFAULT_CANVAS_IMAGE_WIDTH,
  getAssetDropToastMessage,
  type ImageNodesLayout,
  useCommandHistory,
  useEditCanvasStore,
} from '@air/feature-edit-canvas';
import { Portal } from '@air/primitive-portal';
import { useAirModal } from '@air/provider-modal';
import { resetSelectedItemsAction, selectedItemsSelector } from '@air/redux-selected-items';
import { cn } from '@air/utils-tailwind';

import { useCanvasInsertionToastStore } from '~/components/Canvas/store/useCanvasInsertionToastStore';
import { EditClipsTagsModal } from '~/components/Modals/EditClipsTagsModal';
import { SharePrivateBoardModal } from '~/components/SharePrivateBoardModal/SharePrivateBoardModal';
import { SharePrivateCanvasModal } from '~/components/SharePrivateCanvasModal/SharePrivateCanvasModal';
import { SharePrivateClipModal } from '~/components/SharePrivateClipModal/SharePrivateClipModal';
import { CreateSharePrivateCollectionModal } from '~/components/SharePrivateCollectionModal/CreateSharePrivateCollectionModal';
import { BoardsAndAssetsSelectionBar } from '~/components/v2/components/shared/selectionBar/BoardsAndAssetsSelectionBar';
import { CustomFieldsButton } from '~/components/v2/components/shared/selectionBar/CustomFieldsButton';
import { EditTagsButton } from '~/components/v2/components/shared/selectionBar/EditTagsButton';
import { usePrivateSelectionBarMenu } from '~/components/v2/hooks/usePrivateSelectionBarMenu';
import { useSelectionBarDownloads } from '~/components/v2/hooks/useSelectionBarDownloads';
import { useWorkspaceNavPushMode } from '~/components/v2/hooks/useWorkspaceNavPushMode';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { useWorkspaceRightPanelOpen } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelOpen';
import { useWorkspaceRightPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelStore';
import { useWorkspaceSelectionBarStore } from '~/components/v2/store/features/workspace/useWorkspaceSelectionBarStore';
import { Pathname } from '~/constants/pathname';
import { Routes } from '~/constants/routes';
import { useDraftCanvas } from '~/hooks/canvases/useDraftCanvas';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useBulkDownloadPrivateAssets } from '~/hooks/useBulkDownloadPrivateAssets';
import { useCanConsumeMoreCredits } from '~/hooks/useCanConsumeMoreCredits';
import { useClipPermissionsCache } from '~/hooks/useClipPermissionsCache';
import { useDownloadPrivateAsset } from '~/hooks/useDownloadPrivateAsset';
import { useDownloadPrivateBoard } from '~/hooks/useDownloadPrivateBoard';
import { useDownloadPrivateItems } from '~/hooks/useDownloadPrivateItems';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useGetPrivateCustomFieldsMenuOptions } from '~/hooks/useGetCustomFieldsMenuOptions';
import { usePrivateSelectAllTotalCount } from '~/hooks/usePrivateSelectAllTotalCount';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { CentralizedBoardSelectors } from '~/store/centralizedBoard/selectors';
import {
  hasOnlyCanvasesSelectedSelector,
  selectedBoardIdsForCollectionSelector,
  selectedBoardsSelector,
  selectedClipsAssetIdsSelector,
  selectedFileClipsSelector,
  selectedMediaClipsSelector,
} from '~/store/selectedItems/selectors';
import { useToggleFavoritedBoard } from '~/swr-hooks/boards/useToggleFavoritedBoard';
import { useToggleFavorite } from '~/swr-hooks/clips/useToggleFavorite';
import { canChangeAssetCustomFields } from '~/utils/permissions/assetPermissions';
import { canGenerateZip } from '~/utils/permissions/taskPermissions';
import { canCreateCanvasInWorkspace } from '~/utils/permissions/workspacePermissions';
import { useAirStore } from '~/utils/ReduxUtils';

const GRID_GAP = 40;

export const PrivateBoardsAndAssetsSelectionBar = memo(() => {
  const portal = useWorkspaceSelectionBarStore((state) => state.portal);

  const isRightPanelOpen = useWorkspaceRightPanelOpen();
  const { isWorkspaceNavPushMode } = useWorkspaceNavPushMode();

  const { collectionsEnabled } = useFeatureFlags();
  const { selectAllEnabled, generativeCanvasEnabled } = useTemporaryFeatureFlags();

  const dispatch = useDispatch();
  const store = useAirStore();
  const setInsertionToastMessage = useCanvasInsertionToastStore((state) => state.setMessage);
  const canvasReadOnly = useEditCanvasStore((state) => state.readOnly);
  const execute = useCommandHistory((state) => state.execute);
  const selectedMediaClipItems = useSelector(selectedMediaClipsSelector);
  const selectedFileItems = useSelector(selectedFileClipsSelector);
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const resetSelectAll = useViewsSelectAllStore((state) => state.resetSelectAll);
  const selectAllTotalCount = usePrivateSelectAllTotalCount();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const { getClipPermissions } = useClipPermissionsCache();
  const { getBoardPermissions } = useBoardPermissionsCache();
  const { openDraftCanvas } = useDraftCanvas();
  const { canConsumeMoreCredits, showCreditsDepletedModal } = useCanConsumeMoreCredits();

  const canDownloadAllMediaClips = selectedMediaClipItems.every(({ id }) => canGenerateZip(getClipPermissions(id)));
  const canDownloadAllFiles = selectedFileItems.every(({ id }) => canGenerateZip(getClipPermissions(id)));
  const canDownloadAllClips = canDownloadAllMediaClips && canDownloadAllFiles;
  const allSelectedClips = useMemo(
    () => [...selectedMediaClipItems, ...selectedFileItems],
    [selectedMediaClipItems, selectedFileItems],
  );
  const canEditAllCustomFields = useMemo(
    () => allSelectedClips.every(({ id }) => canChangeAssetCustomFields(getClipPermissions(id))),
    [allSelectedClips, getClipPermissions],
  );
  const { getCustomFieldsMenuOptions } = useGetPrivateCustomFieldsMenuOptions();
  const selectAllCustomFieldMenuOptions = useMemo(
    () => (canEditAllCustomFields ? getCustomFieldsMenuOptions({ assets: allSelectedClips }) : []),
    [allSelectedClips, canEditAllCustomFields, getCustomFieldsMenuOptions],
  );

  const selectedBoardItems = useSelector(selectedBoardsSelector);
  const canDownloadAllBoards = selectedBoardItems.every(({ id }) => canGenerateZip(getBoardPermissions(id)));

  const { toggleFavoritedBoard } = useToggleFavoritedBoard();
  const { toggleFavorite } = useToggleFavorite();

  const { createBulkDownloadHandler, createAssetDownloadHandler, createBoardDownloadHandler } =
    useSelectionBarDownloads();
  const { downloadPrivateItems } = useDownloadPrivateItems();
  const { downloadPrivateAsset } = useDownloadPrivateAsset();
  const { downloadPrivateBoard } = useDownloadPrivateBoard();
  const { bulkDownloadPrivateAssets } = useBulkDownloadPrivateAssets();

  const handleBulkDownload = createBulkDownloadHandler({
    downloadItems: downloadPrivateItems,
    bulkDownloadAssets: bulkDownloadPrivateAssets,
  });

  const handleAssetDownload = createAssetDownloadHandler(downloadPrivateAsset);
  const handleBoardDownload = createBoardDownloadHandler(downloadPrivateBoard);

  const [showEditClipsTagsModal] = useAirModal(EditClipsTagsModal);
  const [showEditTagsToClipsModal] = useAirModal(EditClipsTagsModal);
  const [sharePrivateBoard] = useAirModal(SharePrivateBoardModal);
  const [sharePrivateCanvas] = useAirModal(SharePrivateCanvasModal);
  const [sharePrivateClip] = useAirModal(SharePrivateClipModal);
  const [showCreatePrivateCollectionModal] = useAirModal(CreateSharePrivateCollectionModal);

  const { trackShareClicked } = useTrackShareClicked();
  const { trackCanvasMediaImported } = useTrackCanvasMediaImported();

  const handleBoardFavorite = useCallback(
    (board: Board) => {
      toggleFavoritedBoard({ board, trackLocation: 'action-bar' });
    },
    [toggleFavoritedBoard],
  );

  const handleClipFavorite = useCallback(
    (clips: Clip[]) => {
      toggleFavorite({ clips, trackLocation: 'action-bar' });
    },
    [toggleFavorite],
  );

  const handleBoardShare = useCallback(
    (boardId: string) => {
      sharePrivateBoard({ boardId, trackLocation: 'action-bar' });
    },
    [sharePrivateBoard],
  );

  const handleCanvasShare = useCallback(
    (canvasId: string) => {
      sharePrivateCanvas({ canvasId, trackLocation: 'action-bar' });
    },
    [sharePrivateCanvas],
  );

  const handleClipShare = useCallback(
    (clip: Clip, boardContext?: string) => {
      sharePrivateClip({
        clip,
        trackLocation: 'action-bar',
        boardContext,
      });
    },
    [sharePrivateClip],
  );

  const handleSingleClipEditTags = useCallback(
    (clipId: string, tags: Tag[]) => {
      showEditClipsTagsModal({
        clipIds: [clipId],
        trackLocation: 'action-bar',
        initialTags: tags,
      });
    },
    [showEditClipsTagsModal],
  );

  const handleCollectionShare = useCallback(() => {
    trackShareClicked({ location: 'action_bar' });
    showCreatePrivateCollectionModal({
      assetIds: selectedClipsAssetIdsSelector(store.getState()),
      boardIds: selectedBoardIdsForCollectionSelector(store.getState()),
    });
  }, [showCreatePrivateCollectionModal, trackShareClicked, store]);

  const handleMultipleClipsEditTags = useCallback(() => {
    const commonTags = intersectionBy(...allSelectedClips.map(({ tags }) => tags || []), 'id');
    showEditTagsToClipsModal({
      clipIds: allSelectedClips.map(({ id }) => id),
      trackLocation: 'action-bar',
      initialTags: selectAll ? [] : commonTags,
    });
  }, [showEditTagsToClipsModal, allSelectedClips, selectAll]);

  const insertSelectedClipsIntoCanvas = useCallback(() => {
    if (useEditCanvasStore.getState().readOnly) {
      return;
    }
    const allClips = [...selectedMediaClipItems, ...selectedFileItems];
    const allPayloads = allClips.map(clipToCanvasAssetPayload);

    const existingNodes = useEditCanvasStore.getState().nodes;

    const startX =
      existingNodes.length > 0
        ? existingNodes.reduce((max, node) => {
            const width = typeof node.style?.width === 'number' ? node.style.width : DEFAULT_CANVAS_IMAGE_WIDTH;

            return Math.max(max, node.position.x + width);
          }, 0) + GRID_GAP
        : 0;
    const startY = existingNodes[0]?.position.y ?? 0;

    const layout: ImageNodesLayout = {
      kind: 'row',
      startX,
      startY,
      spacing: CANVAS_IMAGE_NODE_SPACING,
    };

    const {
      nodes: nodesToAdd,
      addedCount,
      unsupportedCount,
      videoRejections,
    } = buildNodesFromPayloads(allPayloads, layout);

    if (nodesToAdd.length > 0) {
      execute(commands.addNodes(nodesToAdd, { zoomToFit: false, zoomToFitIfFirstNode: true }));

      const canvasId = useEditCanvasStore.getState().canvasId;
      const sourceBoardId = CentralizedBoardSelectors.boardId(store.getState());

      if (canvasId) {
        trackCanvasMediaImported({
          canvas_id: canvasId,
          number_of_media: nodesToAdd.length,
          source: 'workspace',
          ...(sourceBoardId && { board_id: sourceBoardId }),
        });
      }
    }

    const totalUnsupported = unsupportedCount + selectedBoardItems.length;
    const message = getAssetDropToastMessage(addedCount, totalUnsupported, 'canvas', videoRejections);

    if (message) {
      setInsertionToastMessage(message);
    }

    resetSelectAll();
    dispatch(resetSelectedItemsAction());
  }, [
    dispatch,
    execute,
    resetSelectAll,
    selectedMediaClipItems,
    selectedFileItems,
    selectedBoardItems,
    setInsertionToastMessage,
    store,
    trackCanvasMediaImported,
  ]);

  const handleInsert = useCallback(() => {
    if (!generativeCanvasEnabled || !canConsumeMoreCredits) {
      return;
    }
    insertSelectedClipsIntoCanvas();
  }, [canConsumeMoreCredits, generativeCanvasEnabled, insertSelectedClipsIntoCanvas]);

  const canCreateCanvas = canCreateCanvasInWorkspace(workspacePermissions);

  const handleAddToCanvas = useCallback(() => {
    if (!generativeCanvasEnabled) {
      return;
    }
    if (!canConsumeMoreCredits) {
      showCreditsDepletedModal({ triggeredBy: 'canvas' });
      return;
    }

    const currentBoardId = CentralizedBoardSelectors.boardId(store.getState());
    const selectedItems = selectedItemsSelector(store.getState());
    const selectedClipItems = selectedItems.filter((item) => item.type === 'asset' || item.type === 'file');
    const uniqueSelectedBoardIds = new Set(selectedClipItems.map((item) => item.selectedBoard?.id).filter(Boolean));
    const isMultipleBoards = uniqueSelectedBoardIds.size > 1;

    const allClips = [...selectedMediaClipItems, ...selectedFileItems];
    const allPayloads = allClips.map(clipToCanvasAssetPayload);

    const layout: ImageNodesLayout = {
      kind: 'row',
      startX: 0,
      startY: 0,
      spacing: CANVAS_IMAGE_NODE_SPACING,
    };

    const {
      nodes: initialNodes,
      addedCount,
      unsupportedCount,
      videoRejections,
    } = buildNodesFromPayloads(allPayloads, layout);

    const totalUnsupported = unsupportedCount + selectedBoardItems.length;
    const insertionToastMessage = getAssetDropToastMessage(addedCount, totalUnsupported, 'canvas', videoRejections);

    openDraftCanvas({
      analyticsContext: { source: 'asset_selection_bar' },
      boardId: isMultipleBoards ? undefined : (currentBoardId ?? undefined),
      initialNodes: initialNodes.length > 0 ? initialNodes : undefined,
      insertionToastMessage: insertionToastMessage ?? undefined,
    });

    resetSelectAll();
    dispatch(resetSelectedItemsAction());
  }, [
    canConsumeMoreCredits,
    dispatch,
    generativeCanvasEnabled,
    openDraftCanvas,
    resetSelectAll,
    selectedBoardItems,
    selectedFileItems,
    selectedMediaClipItems,
    showCreditsDepletedModal,
    store,
  ]);

  const { menuOptions, PrefixBarButtons, SuffixBarButtons } = usePrivateSelectionBarMenu({
    collectionsEnabled,
    canDownloadAllClips,
    canDownloadAllBoards,
    onBulkDownload: handleBulkDownload,
    onBoardFavorite: handleBoardFavorite,
    onBoardShare: handleBoardShare,
    onCanvasShare: handleCanvasShare,
    onBoardDownload: handleBoardDownload,
    onClipFavorite: handleClipFavorite,
    onClipShare: handleClipShare,
    onAssetDownload: handleAssetDownload,
    onSingleClipEditTags: handleSingleClipEditTags,
    onMultipleClipsEditTags: handleMultipleClipsEditTags,
    onCollectionShare: handleCollectionShare,
    onAddToCanvas: canCreateCanvas ? handleAddToCanvas : undefined,
    onInsert: canvasReadOnly ? undefined : handleInsert,
    insertCount: selectedMediaClipItems.length + selectedFileItems.length + selectedBoardItems.length,
  });

  const hasOnlyCanvasesSelected = useSelector(hasOnlyCanvasesSelectedSelector);
  const { pathname } = useRouter();

  const isFullscreen = useWorkspaceRightPanelStore((state) => state.isFullscreen);

  const isBoardOrCanvasesPage = pathname === Pathname.BoardByBoardId || pathname === Routes.canvases;

  const hasOnlyCanvasesSelectedRef = useRef(hasOnlyCanvasesSelected);
  hasOnlyCanvasesSelectedRef.current = hasOnlyCanvasesSelected;

  // Clear immediately when landing on a non-canvas page with a canvas-only selection
  useEffect(() => {
    if (hasOnlyCanvasesSelected && !isBoardOrCanvasesPage) {
      resetSelectAll();
      dispatch(resetSelectedItemsAction());
    }
  }, [dispatch, hasOnlyCanvasesSelected, isBoardOrCanvasesPage, resetSelectAll]);

  // Clear on unmount so selections don't bleed into the next page (e.g. canvases → board)
  useEffect(() => {
    return () => {
      if (hasOnlyCanvasesSelectedRef.current) {
        useViewsSelectAllStore.getState().resetSelectAll();
        dispatch(resetSelectedItemsAction());
      }
    };
  }, [dispatch]);

  if (isFullscreen || (hasOnlyCanvasesSelected && !isBoardOrCanvasesPage)) {
    return null;
  }

  const selectionBar = (
    <div className={cn('absolute right-2 bottom-10 left-2 m-2', isWorkspaceNavPushMode ? 'z-40' : 'z-portal')}>
      <BoardsAndAssetsSelectionBar
        menuOptions={selectAll ? [] : menuOptions}
        prefixBarButtons={selectAll ? undefined : <PrefixBarButtons />}
        omitBoardName={isRightPanelOpen}
        stackLeftBarButtons={generativeCanvasEnabled && isRightPanelOpen}
        selectAllEnabled={selectAllEnabled && !hasOnlyCanvasesSelected}
        selectAllTotalCount={selectAllTotalCount}
        suffixBarButtons={
          selectAll ? (
            <>
              <EditTagsButton onClick={handleMultipleClipsEditTags} />
              {selectAllCustomFieldMenuOptions.length > 0 && (
                <CustomFieldsButton options={selectAllCustomFieldMenuOptions} />
              )}
            </>
          ) : (
            <SuffixBarButtons />
          )
        }
      />
    </div>
  );

  return (
    <Portal asChild container={portal}>
      {selectionBar}
    </Portal>
  );
});

PrivateBoardsAndAssetsSelectionBar.displayName = 'PrivateBoardsAndAssetsSelectionBar';
